<template>
  <div>
    <!-- POS Access Required Message -->
    <section v-if="!hasPOSAccess" class="content-container">
      <div class="access-denied-container">
        <h4>POS Access Required</h4>
        <p>
          Inventory management is part of our POS system add-on. Please upgrade
          your subscription to access this feature.
        </p>
        <router-link to="/settings/billing" class="btn btn-primary">
          Upgrade Subscription
        </router-link>
      </div>
    </section>

    <!-- Regular Inventory Content (when access is granted) -->
    <div v-else>
      <section v-if="isRequestingItems">
        <spinner-loader :loading="isRequestingItems" />
      </section>

      <section class="content-container" v-else-if="items.length < 1">
        <p>
          You don't have any items in your inventory yet. <br />Create your
          first one by clicking "New Item."
        </p>
        <b-button @click="$refs.itemModal.open()" style="margin-top: 4px"
          >New Item</b-button
        >
      </section>

      <section v-else class="content-container">
        <div>
          <b-row class="search-header" align-h="center">
            <b-col md="6" class="my-1">
              <b-input-group>
                <b-form-input v-model="filter" :placeholder="$t('search')" />
              </b-input-group>
            </b-col>
            <b-col md="2">
              <b-button @click="$refs.itemModal.open()" style="margin-top: 4px"
                >New Item</b-button
              >
            </b-col>
          </b-row>
        </div>
        <b-table
          v-if="!isLoading"
          :items="items"
          :fields="fields"
          :filter="filter"
          hover
          striped
          @row-clicked="rowClicked"
          class="inventory-table"
          fixed
        >
          <span slot="cell(html)" slot-scope="data" v-html="data.value" />
        </b-table>

        <div v-else class="fixed-center">
          <spinner-loader />
        </div>
      </section>
      <item-modal ref="itemModal" @submit="submitItem" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import ItemModal from "@/components/modals/ItemModal";

export default {
  name: "Inventory",

  components: {
    ItemModal,
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
  },

  data() {
    return {
      items: [],
      fields: [
        {
          key: "name",
          sortable: true,
        },
        {
          key: "description",
          sortable: true,
        },
        {
          key: "default_rate",
          sortable: true,
          formatter: (value) => `$${value}`,
        },
        // {
        //   key: "show_in_db",
        //   label: "Show In Direct Booking",
        //   sortable: true,
        //   formatter: (value) => (value ? "Yes" : "No"),
        // },
      ],
      filter: "",
      isLoading: false,
      selectedItem: null,
    };
  },

  computed: {
    ...mapState({
      isRequestingItems: (state) => state.items.isRequestingItems,
    }),

    ...mapGetters(["getAvailableItemsUnclassified"]),

    ...mapGetters("subscription", ["hasPOSAccess"]),
  },

  async mounted() {
    // Only load items if user has POS access
    if (this.hasPOSAccess) {
      this.isLoading = true;
      await this.getItems();

      this.items = this.getAvailableItemsUnclassified.filter(
        (item) => !item.is_deleted
      );
      this.isLoading = false;
    }
  },

  methods: {
    ...mapActions(["getItems"]),

    rowClicked(item) {
      this.$refs.itemModal.open(item);
    },

    submitItem(submittedItem) {
      const itemId = submittedItem.id;
      const requestType = submittedItem.id ? "updateItem" : "createItem";

      const ogItemTags = submittedItem.og_item_tag || [];
      const itemTags = submittedItem.item_tag;

      delete submittedItem["og_item_tag"];
      delete submittedItem["item_tag"];

      this.$store.dispatch(requestType, submittedItem).then((newItem) => {
        // Handle tag diffs
        let tagPromises = [];
        let deleteTagArray = [];
        let newTagArray = itemTags;

        let newItemId = newItem.id; // Make sure we use the right ID

        for (let tagIndex = 0; tagIndex < ogItemTags.length; tagIndex++) {
          let found = false;

          for (
            let tagArrayIndex = 0;
            tagArrayIndex < newTagArray.length;
            tagArrayIndex++
          ) {
            if (ogItemTags[tagIndex].tag_id == newTagArray[tagArrayIndex].id) {
              newTagArray.splice(tagArrayIndex, 1);
              found = true;
            }
          }

          if (false === found) {
            deleteTagArray.push(ogItemTags[tagIndex].id);
          }
        }

        for (let i = 0; i < newTagArray.length; i++) {
          tagPromises.push(
            this.$store.dispatch("createItemTag", {
              tag_id: newTagArray[i].id,
              item_id: newItemId,
            })
          );
        }

        for (let i = 0; i < deleteTagArray.length; i++) {
          tagPromises.push(
            this.$store.dispatch("deleteItemTag", deleteTagArray[i])
          );
        }

        Promise.all(tagPromises)
          .then(() => {
            return this.$store.dispatch("getItems");
          })
          .catch((err) => {
            this.errorNotification(err);
          });
      });
    },
  },

  watch: {
    getAvailableItemsUnclassified: {
      handler(newItems, oldItems) {
        this.items = newItems.filter((item) => !item.is_deleted);
      },
    },
  },
};
</script>

<style>
.access-denied-container {
  text-align: center;
  padding: 3rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 2rem 0;
}

.access-denied-container h4 {
  color: #dc3545;
  margin-bottom: 1rem;
}

.access-denied-container p {
  color: #6c757d;
  margin-bottom: 1.5rem;
}
</style>
